import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {difference} from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {
  articlePostNews,
  ContentApi,
  fetchDnhb,
  nnhbModels,
  transformNotionContent,
  transformNotionEntry
} from '@dnhb/content-api-client';
import {Models} from "@dnhb/content-api-client/src/config/nnhb/model";
import {transformEntry} from "@dnhb/content-api-client/src/transformer/notion/entry";
import type {ContentApiDef} from '@dnhb/content-api-client';
import {ArticleEntity} from "../contract/article";
import {SourceDef} from "../contract/source";
import {TopicEntity} from "../contract/topic";
import {RESOURCE} from "../const";
import {block, entries, entry} from "../helpers/api/getEntries";
import {slugifyEntry, slugifyTopic} from "../helpers/source";
import {setArticlesCount, setArticlesIndex, setLoading, setOffline, setArticlesUnavailable} from "../redux/applicationActions";
import {setArticles} from "../redux/articleActions";
import {RootState} from "../redux/store";
import {setTopics} from "../redux/topicActions";
import {useContext} from "./useMutableSource";

type useContentApiDef = () => ContentApiDef;

let init = true;

const useContentApi: useContentApiDef = () => {
  const [state, setState] = useContext();
  const dispatch = useDispatch();
  const {base, page_size} = useSelector((state: RootState) => {
    const {config, page_size} = state.application;

    return {
      base: config.base,
      page_size,
    }
  });
  let current: ArticleEntity[] = []; // container
  const loading: string[] = Array.from(Array(2).keys()).map(() => String(uuidv4())); // container

  // bootstrap content API client
  const {contentApi} = state;
  if (!contentApi) {
    const api: ContentApiDef = new ContentApi(
      (source: SourceDef) => block(source, RESOURCE.block),
      fetchDnhb, // source
      transformNotionContent, // transformer
      articlePostNews,
    );

    api.addModel(
      Models.ARTICLE,
      {
        entry: (source: SourceDef) => entry(source, RESOURCE.article),
        entries: (source: SourceDef) => entries(source, RESOURCE.articles),
      },
      fetchDnhb, // source
      transformNotionEntry, // transformer
      null, // adapter //
      {
        Schema: nnhbModels[Models.ARTICLE],
        transformer: {
          slug: slugifyEntry,
        }
      }
    );

    api.addModel(
      Models.TOPIC,
      {
        entry: (source: SourceDef) => entry(source, RESOURCE.topic),
        entries: (source: SourceDef) => entries(source, RESOURCE.topics),
      },
      fetchDnhb, // source
      transformNotionEntry, // transformer
      null, // adapter
      { // options
        Schema: nnhbModels[Models.TOPIC],
        transformer: {
          slug: slugifyTopic,
        }
      }
    );

    setState((prevState) => {
      return {
        ...prevState,
        contentApi: api,
      }
    })
  }

  // load initial content
  useEffect(() => {
    if (!contentApi || !base || !init) return;
    let active = true;
    const [first] = loading;

    const fetch = async (): Promise<{topics: TopicEntity[], articles: ArticleEntity[], articles_count: number}> => {
      if (!active) return {topics: [], articles: [], articles_count: 0};

      // get initial articles
      init = false;
      dispatch(setLoading(loading));
      const articles = await contentApi.getArticles({page: 1, limit: page_size * 2});

      // get topics for initial articles
      const transform = transformEntry.bind(null, contentApi, 'topic');
      const topics = transform(contentApi.options.get('article.meta.topics'));
      const articles_count: number = contentApi.options.get('article.meta.pagination.total');

      return {
        topics,
        articles,
        articles_count,
      }
    }

    //
    fetch().then(({articles = [], topics = [], articles_count = 0}) => {
      current = articles; // eslint-disable-line react-hooks/exhaustive-deps
      dispatch(setArticles(articles));
      dispatch(setArticlesIndex(`${base}/`, articles.map(({id}) => String(id))));
      dispatch(setArticlesCount(`${base}/`, articles_count));
      dispatch(setTopics(topics));
      dispatch(setLoading(difference(loading, [first])));
    }).then(async () => {
      // get all topics
      const topics = await contentApi.getTopics({page: 1, limit: page_size});

      // get articles for all topics
      const transform = transformEntry.bind(null, contentApi, 'article');
      const articles = transform(
        contentApi.options.get('topic.meta.articles')
          .filter(article => article.publication_status !== 'Error')
      );

      return {
        articles,
        topics
      }
    }).then(({articles = [], topics= [] }) => {
      // maintain current order
      const diff = difference(articles.map(({id}) => id), current.map(({id}) => id));
      dispatch(setArticles(articles.filter(({id}) => diff.indexOf(id) > -1)));

      contentApi.articles = [...contentApi.articles, ...articles.filter(({id}) => diff.indexOf(id) > -1)];
      dispatch(setTopics(topics));

      // add articles to topic indexes
      topics.map((topic: TopicEntity) => {
        const {articles: ids} = topic;
        const pathname = `${base}/${topic.slug}/`;
        const all = ids
          .map(id => articles.find(article => String(article.id) === id))
          .sort((a, b) => {
            return (b.publishedTime < a.publishedTime) ? -1 : ((b.publishedTime > a.publishedTime) ? 1 : 0);
          })
          .filter(n => n);

        dispatch(setArticlesIndex(
          pathname,
          all
            .map(({id}) => String(id))
            .slice(0, page_size * 2)
        ));
        dispatch(setArticlesCount(pathname, ids.length));

        if (ids.length <= page_size) {
          dispatch(setArticlesUnavailable(pathname));
        }
      });

      dispatch(setLoading(null));
      dispatch(setOffline(false));
    }).catch((e) => {
      console.error(e);

      // put application in error state
      dispatch(setOffline(true));
    });

    return (): void => {
      active = false;

      return;
    }
  }, [base, contentApi, dispatch, init, page_size]);

  return contentApi;
}

export default useContentApi;
